/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  return 0;
}

function uniqueIds(list = []) {
  return [...new Set((Array.isArray(list) ? list : []).filter(Boolean))];
}

function titleCase(value) {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return "—";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatCount(value) {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num : 0;
}

function formatDate(value) {
  const ms = toMillis(value);
  if (!ms) return "—";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(ms);
  } catch {
    return "—";
  }
}

function getInviteStatus(invite) {
  const status = String(invite?.status || "pending").toLowerCase();

  if (invite?.deletedAt) return "deleted";
  if (invite?.revokedAt || status === "revoked") return "revoked";
  if (invite?.rejectedAt || status === "rejected") return "rejected";
  if (invite?.acceptedAt || status === "accepted") return "accepted";
  if (invite?.approvedAt || status === "approved") return "approved";

  const expiresAtMs = toMillis(invite?.inviteExpiresAt || invite?.expiresAt);
  if (expiresAtMs && expiresAtMs < Date.now()) return "expired";

  return "pending";
}

function getStageTone(status) {
  switch (String(status || "").toLowerCase()) {
    case "accepted":
      return {
        background: "rgba(236, 253, 245, 1)",
        color: "#027A48",
        border: "1px solid rgba(171, 239, 198, 1)",
      };
    case "approved":
      return {
        background: "rgba(239, 248, 255, 1)",
        color: "#175CD3",
        border: "1px solid rgba(191, 219, 254, 1)",
      };
    case "revoked":
    case "rejected":
    case "deleted":
      return {
        background: "rgba(254, 242, 242, 1)",
        color: "#B42318",
        border: "1px solid rgba(254, 205, 202, 1)",
      };
    case "expired":
      return {
        background: "rgba(255, 247, 237, 1)",
        color: "#B54708",
        border: "1px solid rgba(254, 215, 170, 1)",
      };
    default:
      return {
        background: "rgba(255, 250, 235, 1)",
        color: "#B54708",
        border: "1px solid rgba(254, 223, 137, 1)",
      };
  }
}

function getEmailTone(status) {
  const value = String(status || "").toLowerCase();

  if (value === "sent") {
    return {
      background: "rgba(236, 253, 245, 1)",
      color: "#027A48",
      border: "1px solid rgba(171, 239, 198, 1)",
    };
  }

  if (value === "failed") {
    return {
      background: "rgba(254, 242, 242, 1)",
      color: "#B42318",
      border: "1px solid rgba(254, 205, 202, 1)",
    };
  }

  return {
    background: "rgba(248, 250, 252, 1)",
    color: "#475467",
    border: "1px solid rgba(226, 232, 240, 1)",
  };
}

function classNameForId(classMap, classId) {
  const found = classMap[classId];
  return found?.className || found?.name || classId;
}

function StatCard({ label, value, tone = "default" }) {
  const toneMap = {
    default: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,251,255,0.98) 100%)",
      border: "1px solid rgba(219,231,255,0.95)",
      boxShadow: "0 20px 40px rgba(79, 99, 255, 0.08)",
      valueColor: "#112042",
    },
    blue: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(239,245,255,0.98) 100%)",
      border: "1px solid rgba(191,220,255,0.9)",
      boxShadow: "0 20px 40px rgba(23,92,211,0.08)",
      valueColor: "#175CD3",
    },
    success: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(238,252,246,0.98) 100%)",
      border: "1px solid rgba(171,239,198,0.9)",
      boxShadow: "0 20px 40px rgba(2,122,72,0.08)",
      valueColor: "#027A48",
    },
    warning: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,247,236,0.98) 100%)",
      border: "1px solid rgba(254,223,137,0.88)",
      boxShadow: "0 20px 40px rgba(181,71,8,0.08)",
      valueColor: "#B54708",
    },
    danger: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,245,248,0.98) 100%)",
      border: "1px solid rgba(254,205,202,0.88)",
      boxShadow: "0 20px 40px rgba(180,35,24,0.08)",
      valueColor: "#B42318",
    },
  };

  const toneStyle = toneMap[tone] || toneMap.default;

  return (
    <div
      style={{
        ...styles.statCard,
        background: toneStyle.background,
        border: toneStyle.border,
        boxShadow: toneStyle.boxShadow,
      }}
    >
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color: toneStyle.valueColor }}>
        {formatCount(value)}
      </div>
    </div>
  );
}

export default function InviteDashboardSection({ schoolId = "school_001" }) {
  const [invites, setInvites] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [tableSearch, setTableSearch] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const sendStaffInviteV2 = useMemo(
    () => httpsCallable(functions, "sendStaffInviteV2"),
    []
  );

  const approveStaffInvite = useMemo(
    () => httpsCallable(functions, "approveStaffInvite"),
    []
  );

  const classMap = useMemo(() => {
    const map = {};
    classes.forEach((item) => {
      map[item.id] = item;
    });
    return map;
  }, [classes]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const invitesRef = collection(db, "schools", schoolId, "invites");
      const classesRef = collection(db, "schools", schoolId, "classes");

      const [invitesSnap, classesSnap] = await Promise.all([
        getDocs(query(invitesRef, orderBy("updatedAt", "desc"))),
        getDocs(query(classesRef, orderBy("name"))),
      ]);

const inviteRows = invitesSnap.docs
  .map((d) => ({
    id: d.id,
    ...d.data(),
  }))
  .filter((item) => {
    const role = String(item.requestedRole || item.role || "").toLowerCase();
    return role === "teacher" || role === "staff";
  });

      const classRows = classesSnap.docs.map((snap) => ({
        id: snap.id,
        ...snap.data(),
      }));

      setInvites(inviteRows);
      setClasses(classRows);
    } catch (err) {
      console.error("Failed to load invite dashboard:", err);
      setError("Failed to load invite dashboard.");
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const rows = useMemo(() => {
    return invites.map((invite) => {
      const derivedStatus = getInviteStatus(invite);
      const assignedClassIds = uniqueIds(invite.assignedClassIds);
      const assignedClassNames = assignedClassIds.map((id) =>
        classNameForId(classMap, id)
      );

      return {
        ...invite,
        derivedStatus,
        assignedClassIds,
        assignedClassNames,
      };
    });
  }, [invites, classMap]);

  const filteredRows = useMemo(() => {
    const needle = String(tableSearch || "").trim().toLowerCase();

    return rows.filter((item) => {
      const stageMatch =
        stageFilter === "all" ? true : item.derivedStatus === stageFilter;

      if (!stageMatch) return false;
      if (!needle) return true;

      const haystack = [
        item.displayName,
        item.name,
        item.email,
        item.role,
        item.derivedStatus,
        ...(item.assignedClassNames || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(needle);
    });
  }, [rows, tableSearch, stageFilter]);

  const summary = useMemo(() => {
    return rows.reduce(
      (acc, item) => {
        acc.total += 1;
        acc[item.derivedStatus] = (acc[item.derivedStatus] || 0) + 1;

        if (String(item.emailStatus || "").toLowerCase() === "failed") {
          acc.emailFailed += 1;
        }

        return acc;
      },
      {
        total: 0,
        pending: 0,
        accepted: 0,
        approved: 0,
        revoked: 0,
        rejected: 0,
        expired: 0,
        deleted: 0,
        emailFailed: 0,
      }
    );
  }, [rows]);

  async function handleApproveInvite(item) {
    try {
      setBusyId(item.id);
      setError("");
      setSuccess("");

      await approveStaffInvite({
        schoolId,
        inviteId: item.id,
      });

      setSuccess("Invite approved.");
      await loadData();
    } catch (err) {
      console.error("Failed to approve invite:", err);
      setError(err?.message || "Failed to approve invite.");
    } finally {
      setBusyId("");
    }
  }

  async function handleRevoke(item) {
    try {
      setBusyId(item.id);
      setError("");
      setSuccess("");

      await updateDoc(doc(db, "schools", schoolId, "invites", item.id), {
        status: "revoked",
        revokedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSuccess("Invite revoked.");
      await loadData();
    } catch (err) {
      console.error("Failed to revoke invite:", err);
      setError("Failed to revoke invite.");
    } finally {
      setBusyId("");
    }
  }

  async function handleReject(item) {
    try {
      setBusyId(item.id);
      setError("");
      setSuccess("");

      await updateDoc(doc(db, "schools", schoolId, "invites", item.id), {
        status: "rejected",
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSuccess("Invite rejected.");
      await loadData();
    } catch (err) {
      console.error("Failed to reject invite:", err);
      setError("Failed to reject invite.");
    } finally {
      setBusyId("");
    }
  }

  async function handleDelete(item) {
    try {
      setBusyId(item.id);
      setError("");
      setSuccess("");

      await updateDoc(doc(db, "schools", schoolId, "invites", item.id), {
        status: "deleted",
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSuccess("Invite archived.");
      await loadData();
    } catch (err) {
      console.error("Failed to delete invite:", err);
      setError("Failed to delete invite.");
    } finally {
      setBusyId("");
    }
  }

  async function handleResend(item) {
    try {
      setBusyId(item.id);
      setError("");
      setSuccess("");

      await sendStaffInviteV2({
        schoolId,
        inviteId: item.id,
        resend: true,
      });

      setSuccess("Invite resent.");
      await loadData();
    } catch (err) {
      console.error("Failed to resend invite:", err);
      setError(err?.message || "Failed to resend invite.");
    } finally {
      setBusyId("");
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <div style={styles.heroLeft}>
          <div style={styles.heroEyebrow}>PresentPlus Admin</div>
          <h2 style={styles.heroTitle}>Invite Dashboard</h2>
          <p style={styles.heroText}>
            Track onboarding, resend failed invites, and manage the full invite
            lifecycle from one premium control surface.
          </p>
        </div>

        <div style={styles.heroBadge}>
          <div style={styles.heroBadgeLabel}>Live pending invites</div>
          <div style={styles.heroBadgeValue}>{summary.pending}</div>
        </div>
      </div>

      {error ? <div style={styles.errorBox}>{error}</div> : null}
      {success ? <div style={styles.successBox}>{success}</div> : null}

      <section style={styles.statGrid}>
        <StatCard label="Total invites" value={summary.total} tone="default" />
        <StatCard label="Pending" value={summary.pending} tone="warning" />
        <StatCard label="Accepted" value={summary.accepted} tone="success" />
        <StatCard label="Approved" value={summary.approved} tone="blue" />
        <StatCard
          label="Email failed"
          value={summary.emailFailed}
          tone="danger"
        />
      </section>

      <section style={styles.panel}>
        <div style={styles.panelHead}>
          <div>
            <div style={styles.panelEyebrow}>Lifecycle control</div>
            <h3 style={styles.panelTitle}>Staff invitations</h3>
            <div style={styles.panelText}>
              Search, filter, revoke, resend, and track invite state with clear
              status visibility.
            </div>
          </div>
        </div>

        <div style={styles.controlsRow}>
          <div style={styles.searchWrap}>
            <input
              style={styles.searchInput}
              placeholder="Search by email, name, class, or status"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
            />
          </div>

          <div style={styles.filterWrap}>
            {["all", "pending", "accepted", "approved", "revoked", "expired"].map(
              (item) => {
                const active = stageFilter === item;
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setStageFilter(item)}
                    style={{
                      ...styles.filterChip,
                      ...(active ? styles.filterChipActive : {}),
                    }}
                  >
                    {titleCase(item)}
                  </button>
                );
              }
            )}
          </div>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Loading invite records...</div>
        ) : filteredRows.length ? (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Classes</th>
                  <th style={styles.th}>Invite status</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Sent</th>
                  <th style={styles.th}>Accepted</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((item) => {
                  const stageTone = getStageTone(item.derivedStatus);
                  const mailTone = getEmailTone(item.emailStatus);
                  const isBusy = busyId === item.id;

                  return (
                    <tr key={item.id}>
                      <td style={styles.td}>
                        <span style={styles.rolePill}>
                          {titleCase(item.role || item.requestedRole || "teacher")}
                        </span>
                      </td>

                      <td style={styles.td}>
                        {item.assignedClassNames?.length ? (
                          <div style={styles.classList}>
                            {item.assignedClassNames.map((className) => (
                              <span key={`${item.id}-${className}`} style={styles.classPill}>
                                {className}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={styles.mutedText}>None</span>
                        )}
                      </td>

                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            ...stageTone,
                          }}
                        >
                          {titleCase(item.derivedStatus)}
                        </span>
                      </td>

                      <td style={styles.td}>
                        <div style={styles.emailWrap}>
                          <div
                            style={{
                              ...styles.statusBadge,
                              ...mailTone,
                              marginBottom: 8,
                            }}
                          >
                            {titleCase(item.emailStatus || "pending")}
                          </div>
                          <div style={styles.emailText}>
                            {normalizeEmail(item.email || "—")}
                          </div>
                          {item.emailError ? (
                            <div style={styles.emailErrorText}>
                              {String(item.emailError)}
                            </div>
                          ) : null}
                        </div>
                      </td>

                      <td style={styles.td}>{formatDate(item.emailSentAt || item.createdAt)}</td>

                      <td style={styles.td}>{formatDate(item.acceptedAt)}</td>

                      <td style={styles.td}>
                        <div style={styles.actionWrap}>
                          {String(item.emailStatus || "").toLowerCase() === "failed" ||
                          ["pending", "expired", "revoked"].includes(item.derivedStatus) ? (
                            <button
                              type="button"
                              onClick={() => handleResend(item)}
                              disabled={isBusy}
                              style={{
                                ...styles.actionBtn,
                                ...styles.resendBtn,
                                ...(isBusy ? styles.disabledBtn : {}),
                              }}
                            >
                              {isBusy ? "Working..." : "Resend"}
                            </button>
                          ) : null}

                          {item.derivedStatus === "accepted" ? (
                            <button
                              type="button"
                              onClick={() => handleApproveInvite(item)}
                              disabled={isBusy}
                              style={{
                                ...styles.actionBtn,
                                ...styles.approveBtn,
                                ...(isBusy ? styles.disabledBtn : {}),
                              }}
                            >
                              {isBusy ? "Working..." : "Approve"}
                            </button>
                          ) : null}

                          {["pending", "accepted", "approved"].includes(item.derivedStatus) ? (
                            <button
                              type="button"
                              onClick={() => handleRevoke(item)}
                              disabled={isBusy}
                              style={{
                                ...styles.actionBtn,
                                ...styles.revokeBtn,
                                ...(isBusy ? styles.disabledBtn : {}),
                              }}
                            >
                              {isBusy ? "Working..." : "Revoke"}
                            </button>
                          ) : null}

                          {["rejected", "revoked", "expired", "deleted"].includes(
                            item.derivedStatus
                          ) ? (
                            <button
                              type="button"
                              onClick={() => handleDelete(item)}
                              disabled={isBusy}
                              style={{
                                ...styles.actionBtn,
                                ...styles.deleteBtn,
                                ...(isBusy ? styles.disabledBtn : {}),
                              }}
                            >
                              {isBusy ? "Working..." : "Delete"}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.emptyState}>No invites match this view yet.</div>
        )}
      </section>
    </div>
  );
}

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 28,
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    gap: 24,
    padding: "34px 32px",
    borderRadius: 34,
    background: "var(--pp-brand-gradient)",
    boxShadow: "0 28px 56px rgba(79,99,255,0.18)",
    color: "#ffffff",
  },
  heroLeft: {
    maxWidth: 760,
  },
  heroEyebrow: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    fontWeight: 900,
    opacity: 0.92,
    marginBottom: 12,
  },
  heroTitle: {
    margin: 0,
    fontSize: 54,
    lineHeight: 0.96,
    letterSpacing: "-0.05em",
    fontWeight: 900,
  },
  heroText: {
    margin: "18px 0 0 0",
    fontSize: 17,
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.95)",
    maxWidth: 820,
  },
  heroBadge: {
    minWidth: 210,
    alignSelf: "center",
    borderRadius: 28,
    padding: "24px 26px",
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.14)",
    backdropFilter: "blur(10px)",
  },
  heroBadgeLabel: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    opacity: 0.88,
    marginBottom: 14,
  },
  heroBadgeValue: {
    fontSize: 56,
    lineHeight: 1,
    fontWeight: 900,
  },
  errorBox: {
    padding: "16px 18px",
    borderRadius: 18,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#9f1239",
    fontWeight: 800,
  },
  successBox: {
    padding: "16px 18px",
    borderRadius: 18,
    background: "#ecfdf3",
    border: "1px solid #abefc6",
    color: "#027a48",
    fontWeight: 800,
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 18,
  },
  statCard: {
    borderRadius: 28,
    padding: "24px 26px",
    minHeight: 118,
  },
  statLabel: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    color: "#61709a",
    marginBottom: 14,
  },
  statValue: {
    fontSize: 56,
    lineHeight: 1,
    fontWeight: 900,
  },
  panel: {
    borderRadius: 34,
    background: "rgba(255,255,255,0.9)",
    border: "1px solid rgba(221,230,247,0.9)",
    boxShadow: "0 22px 48px rgba(15,23,42,0.06)",
    padding: "30px 28px",
  },
  panelHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 20,
    marginBottom: 22,
  },
  panelEyebrow: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    fontWeight: 900,
    color: "#6b7aa0",
    marginBottom: 12,
  },
  panelTitle: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.05,
    letterSpacing: "-0.03em",
    fontWeight: 900,
    color: "#112042",
  },
  panelText: {
    marginTop: 10,
    color: "#667085",
    fontSize: 16,
    lineHeight: 1.7,
  },
  controlsRow: {
    display: "flex",
    gap: 16,
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
    flexWrap: "wrap",
  },
  searchWrap: {
    flex: "1 1 320px",
    minWidth: 260,
  },
  searchInput: {
    width: "100%",
    height: 58,
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#ffffff",
    padding: "0 18px",
    boxSizing: "border-box",
    fontSize: 16,
    color: "#112042",
    outline: "none",
  },
  filterWrap: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  filterChip: {
    height: 46,
    padding: "0 18px",
    borderRadius: 999,
    border: "1px solid rgba(219,231,255,0.95)",
    background: "#ffffff",
    color: "#24324a",
    fontWeight: 800,
    fontSize: 15,
    cursor: "pointer",
  },
  filterChipActive: {
    background: "var(--pp-brand-gradient)",
    color: "#ffffff",
    border: "1px solid transparent",
    boxShadow: "0 14px 28px rgba(79,99,255,0.18)",
  },
  tableWrap: {
    overflowX: "auto",
    borderRadius: 24,
    border: "1px solid rgba(226,232,240,0.95)",
    background: "#ffffff",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 1100,
  },
  th: {
    textAlign: "left",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    color: "#667085",
    padding: "18px 16px",
    background: "#f8fbff",
    borderBottom: "1px solid rgba(226,232,240,0.95)",
  },
  td: {
    padding: "18px 16px",
    borderBottom: "1px solid rgba(241,245,249,1)",
    color: "#112042",
    fontSize: 15,
    verticalAlign: "top",
  },
  rolePill: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 12px",
    background: "rgba(239,245,255,0.95)",
    border: "1px solid rgba(191,220,255,0.9)",
    color: "#175CD3",
    fontWeight: 800,
  },
  classList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  classPill: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 12px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#334155",
    fontWeight: 700,
    fontSize: 14,
  },
  mutedText: {
    color: "#98a2b3",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 34,
    borderRadius: 999,
    padding: "6px 12px",
    fontWeight: 800,
    fontSize: 14,
  },
  emailWrap: {
    display: "flex",
    flexDirection: "column",
  },
  emailText: {
    color: "#112042",
    wordBreak: "break-word",
  },
  emailErrorText: {
    marginTop: 6,
    color: "#B42318",
    fontSize: 13,
    lineHeight: 1.5,
  },
  actionWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  actionBtn: {
    height: 42,
    padding: "0 16px",
    borderRadius: 14,
    border: "none",
    fontWeight: 800,
    fontSize: 14,
    cursor: "pointer",
  },
  resendBtn: {
    background: "#eef4ff",
    color: "#175CD3",
    border: "1px solid #bfdbfe",
  },
  approveBtn: {
    background: "#ecfdf3",
    color: "#027A48",
    border: "1px solid #abefc6",
  },
  revokeBtn: {
    background: "#fff7ed",
    color: "#B54708",
    border: "1px solid #fed7aa",
  },
  deleteBtn: {
    background: "#fef2f2",
    color: "#B42318",
    border: "1px solid #fecaca",
  },
  disabledBtn: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  emptyState: {
    padding: "26px 8px 8px 8px",
    color: "#667085",
    fontSize: 16,
  },
};